/**
 * Fichier de données géographiques simplifiées des continents.
 *
 * Format : array de polygones (continents), chaque polygone = array de [lon, lat].
 * Le composant fait la projection equirectangular au runtime :
 *   x = (lon + 180) * width / 360
 *   y = (90 - lat) * height / 180
 *
 * Ces contours sont volontairement simplifiés (pas une carte précise) — leur but
 * est de donner un contexte visuel à la heatmap sans dépendance externe.
 */

const NORTH_AMERICA = [
  [-170, 70], [-160, 72], [-145, 70], [-130, 70], [-120, 73], [-100, 75], [-85, 80],
  [-70, 82], [-50, 75], [-55, 60], [-60, 47], [-65, 45], [-70, 42], [-78, 35],
  [-80, 25], [-87, 22], [-95, 18], [-105, 22], [-115, 30], [-122, 35], [-125, 40],
  [-130, 50], [-140, 60], [-160, 65], [-170, 70],
];

const CENTRAL_AMERICA = [
  [-92, 18], [-85, 16], [-80, 8], [-77, 7], [-82, 9], [-86, 12], [-90, 14], [-92, 18],
];

const SOUTH_AMERICA = [
  [-80, 12], [-75, 12], [-65, 8], [-55, 6], [-50, 4], [-40, -2], [-35, -8],
  [-37, -15], [-42, -23], [-50, -32], [-58, -42], [-68, -52], [-72, -55],
  [-72, -40], [-78, -25], [-80, -10], [-82, -5], [-80, 0], [-80, 12],
];

const EUROPE = [
  [-10, 36], [-5, 36], [3, 36], [10, 38], [15, 38], [18, 40], [25, 40],
  [28, 42], [30, 45], [35, 47], [38, 50], [42, 53], [45, 60], [42, 65],
  [30, 68], [20, 70], [10, 70], [0, 68], [-7, 60], [-10, 50], [-10, 42], [-10, 36],
];

const UK = [
  [-8, 50], [-3, 50], [1, 51], [1, 56], [-3, 58], [-7, 56], [-8, 50],
];

const AFRICA = [
  [-17, 14], [-15, 18], [-10, 22], [-5, 30], [10, 35], [25, 32], [33, 30],
  [37, 18], [42, 12], [50, 12], [52, 4], [42, -3], [40, -10], [38, -22],
  [32, -30], [22, -35], [18, -32], [14, -22], [10, -10], [5, -2], [-5, 5],
  [-12, 8], [-17, 14],
];

const MIDDLE_EAST = [
  [35, 28], [50, 28], [55, 25], [55, 12], [50, 15], [42, 15], [35, 28],
];

const ASIA_NORTH = [
  [40, 50], [50, 50], [60, 55], [80, 55], [100, 55], [120, 55], [140, 55],
  [160, 60], [180, 65], [180, 75], [160, 78], [140, 75], [100, 75], [70, 75],
  [40, 70], [40, 50],
];

const ASIA_SOUTH = [
  [40, 30], [50, 30], [55, 25], [60, 23], [70, 23], [78, 28], [85, 30],
  [95, 30], [105, 30], [115, 32], [125, 32], [135, 35], [140, 38], [125, 25],
  [115, 22], [105, 18], [100, 12], [95, 16], [85, 18], [75, 8], [72, 18],
  [65, 25], [55, 22], [50, 22], [45, 24], [40, 30],
];

const SOUTHEAST_ASIA = [
  [95, 5], [100, 8], [105, 12], [115, 5], [120, -2], [115, -8], [105, -8],
  [100, -2], [95, 5],
];

const JAPAN = [
  [129, 31], [135, 33], [142, 38], [145, 43], [142, 45], [137, 38], [131, 33], [129, 31],
];

const AUSTRALIA = [
  [113, -22], [125, -15], [135, -12], [142, -10], [148, -20], [153, -28],
  [148, -38], [140, -38], [130, -32], [120, -33], [115, -32], [113, -22],
];

const NEW_ZEALAND = [
  [170, -40], [175, -38], [178, -45], [173, -47], [170, -40],
];

const ANTARCTICA = [
  [-180, -65], [-90, -68], [0, -70], [90, -68], [180, -65], [180, -85], [-180, -85], [-180, -65],
];

const GREENLAND = [
  [-50, 60], [-30, 65], [-20, 75], [-25, 82], [-50, 82], [-60, 78], [-55, 70], [-50, 60],
];

const MADAGASCAR = [
  [44, -12], [50, -16], [50, -25], [46, -25], [44, -20], [44, -12],
];

const INDONESIA = [
  [95, -2], [120, -2], [140, -2], [140, -8], [120, -8], [95, -8], [95, -2],
];

export const WORLD_POLYGONS = [
  NORTH_AMERICA,
  CENTRAL_AMERICA,
  SOUTH_AMERICA,
  EUROPE,
  UK,
  AFRICA,
  MIDDLE_EAST,
  ASIA_NORTH,
  ASIA_SOUTH,
  SOUTHEAST_ASIA,
  JAPAN,
  AUSTRALIA,
  NEW_ZEALAND,
  ANTARCTICA,
  GREENLAND,
  MADAGASCAR,
  INDONESIA,
];

/**
 * Convert a polygon (array of [lon, lat]) to an SVG path string using
 * equirectangular projection.
 */
export function polygonToPath(polygon, width, height) {
  if (!polygon?.length) return "";
  const projX = (lon) => ((lon + 180) / 360) * width;
  const projY = (lat) => ((90 - lat) / 180) * height;
  const cmds = polygon.map(([lon, lat], i) => `${i === 0 ? "M" : "L"}${projX(lon).toFixed(1)},${projY(lat).toFixed(1)}`);
  cmds.push("Z");
  return cmds.join(" ");
}

export default WORLD_POLYGONS;
